import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { SizeProcess as View } from '@views';
import { routes } from '@constants';
import { LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import { getSizeOptions, isPriceValid, formatPrice } from '@helpers/itemEdit';
import {
  deleteSize, createSize, editSize, detachOptionFromSize,
} from '@store/menu/duck';
import { isClientError } from '@helpers/errorHandling';

const SizeProcess = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const sizeId = location.state?.sizeId;
  const itemId = location.state?.itemId;

  const {
    sizes,
    options,
    editSizePhase,
    deleteSizePhase,
    createSizePhase,
    detachOptionFromSizePhase,
    deleteSizeError,
  } = useSelector(state => state.menu);

  const initialOptions = sizeId && sizes[sizeId] ? sizes[sizeId].optionIds : [];
  const initialName = sizeId && sizes[sizeId] ? sizes[sizeId].name : '';
  const initialPrice = sizeId && sizes[sizeId]
    ? formatPrice((sizes[sizeId].price / 100).toFixed(2))
    : 0;
  const initialPreselectedState = sizeId && sizes[sizeId]
    ? sizes[sizeId].preSelected
    : false;

  const memoizedOptions = useMemo(() => getSizeOptions(sizeId, options, sizes), [sizeId, sizes]);

  const [error, setError] = useState('');
  const [sizeOptions, setSizeOptions] = useState(memoizedOptions);
  const [sizeName, setSizeName] = useState(initialName);
  const [sizePrice, setSizePrice] = useState(initialPrice);
  const [isSizePreselected, setIsSizePreselected] = useState(initialPreselectedState);
  const [hasServerError, setHasServerError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const optionsOrder = sizeOptions.map(option => option.id);

  // Check if size name !== '', size !== 0,

  const isConfirmButtonDisabled = !sizeName;

  // Check if info about size changed

  const isDataTheSame = ((!!sizeId && !!sizes[sizeId] && sizeName === initialName)
  && (JSON.stringify(optionsOrder) === JSON.stringify(initialOptions)
  && parseFloat(sizePrice, 10) * 100 === sizes[sizeId].price)
  && isSizePreselected === initialPreselectedState);

  useEffect(() => {
    if (!location.state) {
      history.goBack();
    }
  }, []);

  useEffect(() => {
    if (deleteSizePhase === SUCCESS) {
      history.push({
        pathname: routes.itemEdit,
        state: {
          id: itemId,
          categoryId: location.state.categoryId,
        },
      });
    }
    if (deleteSizePhase === FAILURE) {
      setIsModalOpen(false);
      if (isClientError(deleteSizeError && deleteSizeError.data.status)) { // Case when we get 4** error
        setError(deleteSizeError.data.error);
      } else {
        setHasServerError(true);
      }
    }
  }, [deleteSizePhase]);

  useEffect(() => {
    if (createSizePhase === SUCCESS || editSizePhase === SUCCESS) {
      history.push({
        pathname: routes.itemEdit,
        state: {
          id: itemId,
          categoryId: location.state.categoryId,
        },
      });
    }
    if (createSizePhase === FAILURE || editSizePhase === FAILURE) {
      setHasServerError(true);
    }
  }, [editSizePhase, createSizePhase]);

  // update size options list after option detach
  useEffect(() => {
    setSizeOptions(memoizedOptions);
  }, [sizes]);

  const handleToastClose = () => {
    setHasServerError(false);
  };

  const handleSizeDelete = () => {
    dispatch(deleteSize({
      id: parseInt(sizeId, 10),
    }));
  };

  const handleEditSubmit = () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.itemEdit,
        state: {
          id: itemId,
          categoryId: location.state.categoryId,
        },
      });
    } else if (sizeId) {
      dispatch(editSize({
        id: parseInt(sizeId, 10),
        price: parseInt(sizePrice * 100, 10), // Convert dolars to cents
        name: sizeName,
        isPreselected: isSizePreselected,
        optionsOrder,
      }));
    } else {
      dispatch(createSize({
        itemId: parseInt(itemId, 10),
        name: sizeName,
        price: parseInt(sizePrice * 100, 10), // Convert dolars to cents
        isPreselected: isSizePreselected,
      }));
    }
  };

  const handleSizePreselectedStateChange = () => {
    setIsSizePreselected(prevState => !prevState);
  };

  const handleSizePriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered price !== 0 to not replace whole value with empty string
      setSizePrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(sizePrice, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleWarningChangeState = () => {
    setIsWarningOpen(prevState => !prevState);
  };

  const handleDragOptionEnd = ({ destination, source }) => {
    if (destination && destination.index !== source.index) {
      const reorderedOptions = [...sizeOptions];
      reorderedOptions.splice(source.index, 1);
      reorderedOptions.splice(destination.index, 0, sizeOptions[source.index]);
      setSizeOptions(reorderedOptions);
    }
  };

  const handleModalChangeState = () => {
    setIsModalOpen(prevState => !prevState);
  };

  const handleEditCancel = () => {
    history.push({
      pathname: routes.itemEdit,
      state: {
        id: itemId,
        categoryId: location.state.categoryId,
      },
    });
  };

  const handleSizeNameChange = e => {
    setSizeName(e.target.value);
  };

  const handleOptionAdd = () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.optionsList,
        state: {
          sizeId,
          itemId,
          categoryId: location.state.categoryId,
        },
      });
    } else {
      setIsWarningOpen(true);
    }
  };

  const handleOptionDetach = optionId => () => {
    dispatch(detachOptionFromSize({
      sizeId,
      optionId,
    }));
  };

  const handleOptionEdit = optionId => () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.optionEdit,
        state: {
          sizeId,
          id: optionId,
          itemId,
          categoryId: location.state.categoryId,
        },
      });
    } else {
      setIsWarningOpen(true);
    }
  };

  const isLoading = deleteSizePhase === LOADING
    || createSizePhase === LOADING
    || editSizePhase === LOADING
    || detachOptionFromSizePhase === LOADING;

  return (
    <View
      isDataTheSame={isDataTheSame}
      onWarningChangeState={handleWarningChangeState}
      isWarningOpen={isWarningOpen}
      onOptionEdit={handleOptionEdit}
      error={error}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      isLoading={isLoading}
      onSizePreselectedStateChange={handleSizePreselectedStateChange}
      isSizePreselected={isSizePreselected}
      sizePrice={sizePrice.toString()}
      onSizePriceChange={handleSizePriceChange}
      onDragOptionEnd={handleDragOptionEnd}
      isEditing={!!sizeId}
      isModalOpen={isModalOpen}
      onSizeDelete={handleSizeDelete}
      onModalChangeState={handleModalChangeState}
      onOptionAdd={handleOptionAdd}
      options={sizeOptions}
      onEditCancel={handleEditCancel}
      onToastClose={handleToastClose}
      hasServerError={hasServerError}
      sizeName={sizeName}
      onSizeNameChange={handleSizeNameChange}
      onEditSubmit={handleEditSubmit}
      onOptionDetach={handleOptionDetach}
    />
  );
};

export default SizeProcess;
